import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button, Input } from '../input';
import { Error } from '../error';
import { Loading } from '../loading';
import { Modal } from '../modal';
import { track } from '../track';
import { useGlobal } from '../../state';
import { stripePublishableKey } from '../../constants';

const stripeElementStyle = {
  border: '1px solid #eee',
  borderRadius: 8,
  padding: 12,
  background: '#fff',
};

const summaryStyle = {
  border: '1px solid #eee',
  borderRadius: 8,
  padding: 12,
  background: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 13,
};

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
};

const presetButtonStyle = {
  border: '1px solid #eee',
  borderRadius: 4,
  background: '#eee',
  color: '#444',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 'bold',
  height: 24,
  padding: '4px 8px',
  width: 58,
};

const money = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const CheckoutForm = ({ plan, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { fox, refreshUser } = useGlobal();
  const [amount, setAmount] = useState(plan.id == 'payg' ? 5 : 10);
  const [ready, setReady] = useState(false);
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const chargeAmount = plan.id == 'monthly_10' ? 10 : parseInt(amount, 10);
  const canSubmit = stripe && elements && ready && complete && chargeAmount > 0 && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }

    setError(null);
    setLoading(true);
    track('billing/checkout_submit_click', {
      plan: plan.id,
      amount: chargeAmount,
    });

    try {
      const { setupIntent, error: stripeError } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        track('billing/stripe_confirm_error', {
          plan: plan.id,
          amount: chargeAmount,
          message: stripeError.message,
        });
        setError(stripeError.message || 'Unable to confirm card.');
        return;
      }

      if (!setupIntent?.payment_method) {
        track('billing/stripe_confirm_error', {
          plan: plan.id,
          amount: chargeAmount,
          message: 'missing_payment_method',
        });
        setError('Unable to save payment method.');
        return;
      }

      track('billing/stripe_confirm_success', {
        plan: plan.id,
        amount: chargeAmount,
      });

      await fox.call('POST', '/api/billing/setup-and-charge', {
        plan: plan.id,
        amount: chargeAmount,
        paymentMethodId: setupIntent.payment_method,
      });

      await refreshUser();
      track('billing/checkout_success', {
        plan: plan.id,
        amount: chargeAmount,
      });
      toast.info(plan.id == 'payg' ? 'Purchased credits' : 'Monthly subscription started');
      onSuccess();
    } catch (e) {
      track('billing/checkout_error', {
        plan: plan.id,
        amount: chargeAmount,
        message: String(e?.error || e?.message || e),
      });
      setError(String(e?.error || e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 20, fontWeight: 'bold' }}>{plan.checkoutTitle}</div>
        <div style={{ color: '#666', lineHeight: 1.45 }}>{plan.checkoutDescription}</div>
      </div>

      {plan.id == 'payg' && (
        <div>
          <Input
            label="Credits to buy"
            type="number"
            min="1"
            max="500"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{ width: '100%' }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {[5, 25, 50, 100].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  track('billing/amount_preset_click', {
                    plan: plan.id,
                    amount: value,
                  });
                  setAmount(value);
                }}
                style={presetButtonStyle}
              >
                ${value}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={summaryStyle}>
        <div style={rowStyle}>
          <span>{plan.title}</span>
          <strong>{money(chargeAmount)}</strong>
        </div>
        {plan.id == 'monthly_10' && (
          <div style={{ color: '#666' }}>First month only. Future monthly billing will be added later.</div>
        )}
      </div>

      <div style={stripeElementStyle}>
        <PaymentElement onReady={() => setReady(true)} onChange={(e) => setComplete(e.complete)} />
      </div>

      <Button loading={loading} disabled={!canSubmit} style={{ width: '100%' }}>
        {plan.id == 'payg' ? `Pay ${money(chargeAmount)}` : 'Pay $10.00'}
      </Button>
      <Error message={error} />
    </form>
  );
};

const CheckoutBody = ({ plan, onSuccess }) => {
  const { fox } = useGlobal();
  const [clientSecret, setClientSecret] = useState();
  const [error, setError] = useState();
  const stripePromise = useMemo(() => loadStripe(stripePublishableKey), []);

  useEffect(() => {
    if (!fox) {
      return;
    }

    let mounted = true;
    setError(null);
    setClientSecret(null);

    fox
      .call('POST', '/api/user/stripe/payment-intent')
      .then((data) => {
        if (mounted) {
          setClientSecret(data.clientSecret);
        }
      })
      .catch((e) => {
        if (mounted) {
          setError(String(e?.error || e?.message || e));
        }
      });

    return () => {
      mounted = false;
    };
  }, [fox]);

  if (error) {
    return <Error message={error} />;
  }

  if (!clientSecret) {
    return <Loading center />;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutForm plan={plan} onSuccess={onSuccess} />
    </Elements>
  );
};

export const BillingCheckoutModal = ({ plan, children, onSuccess }) => {
  const [closeSignal, setCloseSignal] = useState();

  const handleSuccess = () => {
    setCloseSignal(Date.now());
    onSuccess?.();
  };

  return (
    <Modal title={plan.title} close={closeSignal} innerStyle={{ width: 560 }}>
      {children}
      <CheckoutBody plan={plan} onSuccess={handleSuccess} />
    </Modal>
  );
};
